import { computed, type Ref } from 'vue';
import type { RouteLocationNormalizedLoadedGeneric } from 'vue-router';
import { facilityFromRouteOrLastVisited, yearFromRouteOrLastVisited } from '@/router';
import { slugFromName } from '../../../tri-shared/services/formatting';
import { navActiveStatesUpdate, type NavMenuGroup, type NavMenuSection } from '../../../cross-app-frontend/navigation';

import { facilityPermissions } from '@/services/navigation/navigation-permissions';

export function useEmissionsNavMenu(currentRoute: Ref<RouteLocationNormalizedLoadedGeneric>): Ref<NavMenuSection[]> {
  return computed((): NavMenuSection[] => {
    const facility = facilityFromRouteOrLastVisited.value;
    if (!facility) {
      return [];
    }

    const facilityKey = facility.facilityID as unknown as number;
    const perms = facilityPermissions.value[facilityKey];
    const hasUpdateBaseFacts = perms?.hasUpdateBaseFacts ?? false;
    const hasUpdateFacility = perms?.hasUpdateFacility ?? false;
    const hasAdministerFacility = perms?.hasAdministerFacility ?? false;
    const hasManageUsers = perms?.hasManageUsers ?? false;

    const isAdmin = hasAdministerFacility;
    const isSupervisor = !isAdmin && hasUpdateFacility;
    const isDataPreparer = !isAdmin && !isSupervisor && hasUpdateBaseFacts;

    const facilityParams = {
      facilityID: facility.facilityID,
      facilitySlug: slugFromName(facility.name),
      year: yearFromRouteOrLastVisited.value,
    };

    let workflowGroups: NavMenuGroup[] = [
      {
        title: 'Emissions',
        children: [
          {
            title: 'Overview',
            icon: 'fa-chart-mixed',
            type: 'link',
            isActive: false,
            to: { name: 'Emissions Overview', params: facilityParams },
          },
          {
            title: 'Units',
            icon: 'fa-industry-windows',
            type: 'link',
            isActive: false,
            to: { name: 'Emissions Units', params: facilityParams },
          },
          {
            title: 'My Products',
            icon: 'fa-boxes-stacked',
            type: 'link',
            isActive: false,
            to: { name: 'Emissions My Products', params: facilityParams },
          },
          {
            title: 'Questionnaire',
            icon: 'fa-file-circle-question',
            type: 'link',
            isActive: false,
            to: {
              name: 'Emissions Questionnaire',
              params: {
                ...facilityParams,
                emissionsUnitID: '0',
                emissionsUnitSlug: 'pick-one',
              },
            },
          },
          {
            title: 'Emissions',
            icon: 'fa-chart-line-up',
            type: 'link',
            isActive: false,
            to: {
              name: 'Emissions',
              params: {
                ...facilityParams,
                emissionsUnitID: '0',
                emissionsUnitSlug: 'pick-one',
              },
            },
            activeRouteNames: ['Potential Emissions'],
          },
          {
            title: 'Limits',
            icon: 'fa-shield-check',
            type: 'link',
            isActive: false,
            to: { name: 'Emissions Limits', params: facilityParams },
            activeRouteNames: ['Emissions Limit History'],
          },
          {
            title: 'Reports',
            icon: 'fa-table-columns',
            type: 'link',
            isActive: false,
            to: {
              name: 'Emissions Reports',
              params: {
                ...facilityParams,
                emissionsUnitID: '0',
                emissionsUnitSlug: 'pick-one',
              },
            },
          },
        ],
      },
    ];

    if (isDataPreparer) {
      // Data Preparers: Overview and Questionnaire only.
      workflowGroups = workflowGroups
        .map((group) => ({
          ...group,
          children: group.children.filter(
            (child) =>
              child.type === 'link' &&
              child.to &&
              typeof child.to === 'object' &&
              'name' in child.to &&
              child.to.name !== 'Emissions Units' &&
              child.to.name !== 'Emissions Limits',
          ),
        }))
        .filter((group) => group.children.length > 0);
    }

    const adminGroups: NavMenuGroup[] = [
      {
        title: 'Admin',
        children: [
          {
            title: 'Emissions Unit Types',
            icon: 'fa-layer-plus',
            type: 'link',
            isActive: false,
            to: { name: 'Emissions Unit Types', params: facilityParams },
          },
          {
            title: 'Products',
            icon: 'fa-boxes-stacked',
            type: 'link',
            isActive: false,
            to: { name: 'Facility Products', params: facilityParams },
          },
          {
            title: 'Release Profiles',
            icon: 'fa-clouds',
            type: 'link',
            isActive: false,
            to: { name: 'Release Profiles', params: facilityParams },
          },
          {
            title: 'Compliance Metrics',
            icon: 'fa-function',
            type: 'link',
            isActive: false,
            to: { name: 'Compliance Metrics', params: facilityParams },
          },
          {
            title: 'Chemicals',
            icon: 'fa-flask-vial',
            type: 'link',
            isActive: false,
            to: { name: 'Chemicals', params: facilityParams },
          },
          {
            title: 'Facilities',
            icon: 'fa-city',
            type: 'link',
            isActive: false,
            to: { name: 'Facilities', params: facilityParams },
          },
          {
            title: 'Users',
            icon: 'fa-users',
            type: 'link',
            isActive: false,
            to: { name: 'Users', params: facilityParams },
          },
        ],
      },
    ];

    const result: NavMenuSection[] = [
      {
        id: 'workflow',
        title: 'Emissions Workflow',
        groups: workflowGroups,
      },
      ...(isAdmin || hasManageUsers
        ? [
            {
              id: 'admin',
              title: 'Admin',
              groups: adminGroups,
            } as NavMenuSection,
          ]
        : [
            {
              id: 'admin',
              title: 'Admin',
              groups: [
                {
                  title: 'Admin',
                  children: [
                    {
                      title: 'Products',
                      icon: 'fa-boxes-stacked',
                      type: 'link',
                      isActive: false,
                      to: { name: 'Facility Products', params: facilityParams },
                    },
                  ],
                },
              ],
            } as NavMenuSection,
          ]),
    ];

    navActiveStatesUpdate(currentRoute.value, result);

    return result;
  });
}
